// How the sign-in verification code reached the user, carried from the password
// step to the OTP screen.
//
// The login page remounts between the password step and the OTP step (and after
// every resend or wrong code), and the auth reducer resets on each of those
// transitions, so localStorage is the hand-off channel, the same way `needs2FA`,
// `otp_auth_token` and `phone_number` already travel. Nothing here is secret: the
// values only choose which copy and fallback links the OTP screen shows.

export const OTP_DELIVERY = {
  authenticatorApp: 'authenticator_app',
  email: 'email',
  sms: 'sms',
};

// Sent on the sign-in POST so the server knows this client can prompt for an
// authenticator-app code. A header rather than a body param: the sessions
// controller turns unpermitted params into a 401, which would break sign-in if a
// new bundle reached an old backend during a rolling deploy.
export const OTP_METHODS_HEADER = 'X-Otp-Methods';
export const SUPPORTED_OTP_METHODS = OTP_DELIVERY.authenticatorApp;

export const OTP_DELIVERY_STORAGE_KEY = 'otp_delivery';
export const AUTHENTICATOR_APP_ENABLED_STORAGE_KEY = 'authenticator_app_enabled';
export const OTP_DELIVERY_STORAGE_KEYS = [
  OTP_DELIVERY_STORAGE_KEY,
  AUTHENTICATOR_APP_ENABLED_STORAGE_KEY,
];

const KNOWN_DELIVERIES = Object.values(OTP_DELIVERY);

// Anything the server did not send (an older backend) or that we do not
// recognise becomes null, which keeps the existing email/SMS copy.
export const normalizeOtpDelivery = (value) => (
  KNOWN_DELIVERIES.includes(value) ? value : null
);

export const clearOtpDelivery = () => {
  OTP_DELIVERY_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage can be unavailable (private mode, quota); the OTP screen then
      // falls back to the existing email/SMS copy.
    }
  });
};

export const saveOtpDelivery = ({ otpDelivery, authenticatorAppEnabled }) => {
  try {
    const delivery = normalizeOtpDelivery(otpDelivery);
    if (delivery) {
      localStorage.setItem(OTP_DELIVERY_STORAGE_KEY, delivery);
    } else {
      localStorage.removeItem(OTP_DELIVERY_STORAGE_KEY);
    }
    localStorage.setItem(AUTHENTICATOR_APP_ENABLED_STORAGE_KEY, authenticatorAppEnabled === true ? 'true' : 'false');
  } catch {
    // See clearOtpDelivery.
  }
};

// Records a resend through email or SMS without touching whether the user also
// has an authenticator app (that fact only comes from the password step).
export const saveResentOtpDelivery = (otpDelivery) => {
  try {
    const delivery = normalizeOtpDelivery(otpDelivery);
    if (delivery) localStorage.setItem(OTP_DELIVERY_STORAGE_KEY, delivery);
  } catch {
    // See clearOtpDelivery.
  }
};

export const loadOtpDelivery = () => {
  try {
    return {
      otpDelivery: normalizeOtpDelivery(localStorage.getItem(OTP_DELIVERY_STORAGE_KEY)),
      authenticatorAppEnabled: localStorage.getItem(AUTHENTICATOR_APP_ENABLED_STORAGE_KEY) === 'true',
    };
  } catch {
    return { otpDelivery: null, authenticatorAppEnabled: false };
  }
};
